import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type ReactECharts from 'echarts-for-react';
import type { VisibilityReportResponse } from '@brand24-original/llm-types';
import {
  ReportCompetitiveRanking,
  ReportBrandPresence,
  ReportCompetitorMatrix,
  ReportContextGaps,
  ReportCover,
  ReportFunnel,
  ReportPerformanceTrend,
  ReportPlatformPerformance,
  ReportPromptAnalysis,
  ReportShareOfVoice,
  ReportSourceDomains,
  ReportSourcePages,
  ReportSummary,
  ReportTopicShare,
} from '../../../shared/components/report';
import { buildBrandColors } from '../../../shared/components/report/brandColors';
import { brandLabel } from '../../../shared/components/report/brandLabel';
import { localizeReportTopics } from '../../../shared/components/report/localizeReportTopics';
import {
  formatPeriodLabel,
  periodLocaleFor,
} from '../../../shared/helpers/filters/formatPeriodLabel';

interface VisibilityReportSlidesProps {
  report: VisibilityReportResponse;
  /** Tylko dla stron BEZ własnego nagłówka (publiczny link) — wtedy przycisk PDF siedzi na okładce. */
  onDownloadPdf?: () => void;
  isDownloading?: boolean;
}

/**
 * Złożony raport klienta — jeden zestaw slajdów dla widoku w panelu i dla publicznego linku, żeby
 * odbiorca linku widział dokładnie to samo, co klient u siebie. Pobieranie PDF-a trzyma strona:
 * w panelu przycisk stoi w nagłówku, przy publicznym linku — na okładce.
 */
const VisibilityReportSlides: React.FC<VisibilityReportSlidesProps> = ({
  report: rawReport,
  onDownloadPdf,
  isDownloading,
}) => {
  const { i18n } = useTranslation();
  const report = useMemo(() => localizeReportTopics(rawReport, i18n.language), [rawReport, i18n.language]);
  // The PDF is rendered by the backend, so no chart is captured any more. The ranking still gets a
  // ref because with one BrandVisibilityBarChart keeps the bar chart on phones (without it, it
  // switches to its list layout) — the report looks as it did when the PDF was captured here.
  const rankingChartRef = useRef<ReactECharts>(null);
  // Jedna mapa kolorów na cały raport — kolejność Share of Voice jest kanoniczna.
  const brandColors = useMemo(
    () =>
      buildBrandColors(
        report.shareOfVoice.map((entry) => ({
          brand: brandLabel(entry.brand),
          isTracked: entry.isTracked,
        }))
      ),
    [report]
  );

  return (
    <>
      <ReportCover
        brand={report.brand}
        generatedAt={report.generatedAt}
        promptsAnalyzed={report.promptsAnalyzed}
        responsesAnalyzed={report.responsesAnalyzed}
        models={report.visibilityPerModel.map((entry) => entry.model)}
        periodLabel={formatPeriodLabel(
          report.periodFrom,
          report.periodTo,
          periodLocaleFor(i18n.language)
        )}
        onDownloadPdf={onDownloadPdf}
        isDownloading={isDownloading}
      />
      <ReportSummary brand={report.brand} score={report.score} />
      <ReportPerformanceTrend trend={report.trend} periodType={report.periodType} />
      <ReportPlatformPerformance models={report.visibilityPerModel} />
      <ReportCompetitiveRanking
        brands={report.brandVisibility}
        brandColors={brandColors}
        chartRef={rankingChartRef}
      />
      <ReportFunnel funnel={report.funnel} brand={report.brand} />
      <ReportShareOfVoice brands={report.shareOfVoice} brandColors={brandColors} />
      <ReportCompetitorMatrix matrix={report.competitorMatrix} brandColors={brandColors} />
      <ReportTopicShare topics={report.topicShares} />
      <ReportBrandPresence presence={report.brandPresence} brandColors={brandColors} />
      <ReportContextGaps gaps={report.contextGaps} />
      <ReportSourceDomains sources={report.sources} />
      <ReportSourcePages pages={report.sources.topPages} />
      <ReportPromptAnalysis prompts={report.prompts} />
    </>
  );
};

export default VisibilityReportSlides;
